type CalendarOption = Record<string, unknown> & {
  schedule?: Array<Record<string, unknown>> | null;
  hours?: Array<Record<string, unknown>> | null;
  price?: string | null;
  priceLocalized?: Record<string, unknown> | null;
  scheduleLoaded?: boolean;
  scheduleLoading?: boolean;
  scheduleError?: string | null;
  scheduleVersion?: number | null;
};

type DoctorEntry = Record<string, unknown> & {
  bookingOptions?: CalendarOption[] | null;
  totalDatesMax?: number | null;
  totalTimesMax?: number | null;
  baseDateIso?: string | null;
  scheduleVersion?: number | null;
};

type CalendarResult = {
  schedule: Array<Record<string, unknown>>;
  hours: Array<Record<string, unknown>>;
  priceLabel?: string | null;
  priceLocalized?: Record<string, unknown> | null;
  scheduleMaxTimes?: number;
  totals: { dates?: number; times?: number };
  baseDateIso?: string | null;
  window?: Record<string, unknown> | null;
  windowDate?: Date | null;
};

type ApplyCalendarResultParams = {
  doctorEntry: DoctorEntry;
  optionIndex: number;
  calendarResult: CalendarResult;
  fallbackBaseDateIso?: string | null;
  ensureCenterVariantKeys?: (doctor: DoctorEntry, option: CalendarOption) => void;
};

export function applyCalendarResultToDoctor({
  doctorEntry,
  optionIndex,
  calendarResult,
  fallbackBaseDateIso,
  ensureCenterVariantKeys,
}: ApplyCalendarResultParams) {
  const bookingOptions = Array.isArray(doctorEntry.bookingOptions)
    ? [...doctorEntry.bookingOptions]
    : [];
  const originalOption = bookingOptions[optionIndex];
  if (!originalOption) {
    return null;
  }

  const targetDoctor = { ...doctorEntry } as DoctorEntry;

  // Preserve the catalog price established at search time. The calendar fetch is about
  // availability, not re-pricing the booking type: its priceLabel can reflect a per-slot
  // or minimum-duration price (e.g. 30 min) and must not silently override the type price.
  const hasOriginalPrice =
    typeof originalOption.price === 'string' && originalOption.price.trim().length > 0;
  const resolvedPriceLabel = hasOriginalPrice ? originalOption.price : calendarResult.priceLabel;
  const resolvedPriceLocalized =
    originalOption.priceLocalized !== undefined && originalOption.priceLocalized !== null
      ? originalOption.priceLocalized
      : calendarResult.priceLocalized;

  const targetOption = {
    ...originalOption,
    schedule: calendarResult.schedule,
    hours: calendarResult.hours,
    price: resolvedPriceLabel,
    priceLocalized: resolvedPriceLocalized,
    calendarWindow: calendarResult.window ?? null,
    scheduleLoaded: true,
    scheduleLoading: false,
    scheduleError: null,
  } as CalendarOption;

  const effectiveDates = calendarResult.totals.dates || calendarResult.schedule.length || 0;
  const effectiveTimes = Math.max(calendarResult.totals.times || 0, calendarResult.scheduleMaxTimes || 0);

  targetDoctor.totalDatesMax = effectiveDates;
  targetDoctor.totalTimesMax = effectiveTimes;

  const baseDateIso = calendarResult.baseDateIso || fallbackBaseDateIso || targetDoctor.baseDateIso || null;
  targetDoctor.baseDateIso = baseDateIso;

  const nextScheduleVersion = (Number(targetDoctor.scheduleVersion) || 0) + 1;
  targetDoctor.scheduleVersion = nextScheduleVersion;
  targetOption.scheduleVersion = nextScheduleVersion;

  if (typeof ensureCenterVariantKeys === 'function') {
    ensureCenterVariantKeys(targetDoctor, targetOption);
  }

  bookingOptions.splice(optionIndex, 1, targetOption);
  targetDoctor.bookingOptions = bookingOptions;

  return {
    doctor: targetDoctor,
    option: targetOption,
    scheduleVersion: nextScheduleVersion,
  };
}

export function applyCalendarErrorToDoctor(doctorEntry: DoctorEntry, optionIndex: number, message: string) {
  const bookingOptions = Array.isArray(doctorEntry.bookingOptions)
    ? [...doctorEntry.bookingOptions]
    : [];
  const originalOption = bookingOptions[optionIndex];
  if (!originalOption) {
    return null;
  }

  const targetDoctor = { ...doctorEntry } as DoctorEntry;
  const targetOption = {
    ...originalOption,
    scheduleLoading: false,
    scheduleError: message || 'Calendar unavailable',
  } as CalendarOption;

  bookingOptions.splice(optionIndex, 1, targetOption);
  targetDoctor.bookingOptions = bookingOptions;

  return {
    doctor: targetDoctor,
    option: targetOption,
  };
}
